import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Card from "@mui/material/Card";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import MuiTextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { fmtDateTime } from "../../design/domain";
import ListPager from "../../components/design/ListPager";
import { EmptyState } from "../../components/mui";
import { PageLoading, SkeletonRows } from "../../components/loading";
import { useApp } from "../../design/AppContext";
import { apiDelete, apiGet, apiPostJson, apiPutJson } from "../../utils/api";
import { apiErrorMessage } from "../../utils/apiError";
import { hasPermission } from "../../utils/permission";
import request from "../../utils/request";
import { getToken } from "../../utils/token";
import {
  ITEM_STATUS,
  RISK,
  isActiveStatus,
  fmtDuration,
  fmtCount,
  safeHttpUrl,
  stripHtml,
  parseListLines,
  MetaPill,
  RunStatusPill,
  IconButton,
  KpiRow,
  ScanDrawer,
  MetaGrid,
  ErrorNote,
  Disclosure,
  LogTail,
  SectionHead,
  CountChip,
  HeadNote,
  tableBodySx,
  useScanPage,
  useRunHistory,
  useRunReport,
} from "../../components/scan/ScanKit";
import { MdChevronRight, MdDownload, MdPlayArrow } from "react-icons/md";

/**
 * 專案弱點掃描（OWASP ZAP）
 *
 * 結構與 CodeScan/Index.js 相同（選專案 → 維護網址 → 開始掃描 → 歷史與報告），差在：
 * - 清單是網站網址而不是 Git repo；
 * - 多一個掃描模式：基本（爬網＋被動分析）／完整（再加主動攻擊測試）。
 *   完整掃描會真的對目標送出攻擊請求，按下去之前一定要再確認一次。
 * - 報告除了畫面上的告警，每個網址還能下載 ZAP 產生的完整 HTML 報告。
 */

const BASE = "/VulnScan";

/* ===================== 樣式 ===================== */

const MONO = { fontFamily: "var(--font-mono)" };
const FIELD_LABEL_SX = { fontWeight: 800, color: "text.secondary", mb: 0.5 };
const LINK_SX = { fontWeight: 600, wordBreak: "break-all" };

/* ===================== 對照表 ===================== */

/** scan_mode：0 基本、1 完整。完整掃描的膠囊用橘色，歷史裡一眼分得出哪幾次打過攻擊請求 */
const MODE = {
  0: { label: "基本掃描", desc: "爬網＋被動分析，不送出攻擊請求", pill: { label: "基本", kind: "type" } },
  1: { label: "完整掃描", desc: "加上主動攻擊測試", pill: { ...RISK[2], label: "完整" } },
};

/** 四個風險數欄位，高 → 低 */
const RISK_FIELDS = [
  { key: "high_count", risk: 3, label: "高風險" },
  { key: "medium_count", risk: 2, label: "中風險" },
  { key: "low_count", risk: 1, label: "低風險" },
  { key: "info_count", risk: 0, label: "資訊" },
];

const CONFIDENCE = { High: "高", Medium: "中", Low: "低", Confirmed: "已確認", "False Positive": "誤判" };

/* ===================== 工具 ===================== */

const targetLine = (t) => (t.name ? `${t.url} ${t.name}` : t.url);
const targetsToText = (targets) => targets.map(targetLine).join("\n");

/** 判斷有沒有未儲存的變更時，略過註解與空行 */
const normalizedDraft = (text) =>
  parseListLines(text)
    .map((x) => (x.rest ? `${x.url} ${x.rest}` : x.url))
    .join("\n");

/** 有風險才上色；「資訊」不算風險，維持一般字色 */
const riskColor = (v, risk) => (Number(v) > 0 && risk > 0 ? RISK[risk].c : undefined);

/* stripHtml 已經搬到 components/scan/ScanKit.js —— 新的「檢測」合併頁也要用同一份 */

/* ===================== 報告抽屜 ===================== */

/** 「高 1　中 4　低 9　資訊 3」一行 */
function RiskCounts({ row }) {
  return (
    <Typography
      component="span"
      variant="body2"
      sx={{ display: "inline-flex", gap: 1.5, flexWrap: "wrap", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
    >
      {RISK_FIELDS.map((f) => (
        <Box component="span" key={f.key} sx={{ whiteSpace: "nowrap", color: riskColor(row[f.key], f.risk) || "text.secondary" }}>
          {RISK[f.risk].label} {fmtCount(row[f.key])}
        </Box>
      ))}
    </Typography>
  );
}

RiskCounts.propTypes = { row: PropTypes.object };
RiskCounts.defaultProps = { row: {} };

/** 告警的一段說明文字（標籤＋內容）；名稱沿用原本的 TextField，MUI 的 TextField 在這個檔改名 MuiTextField */
function TextField({ label, text }) {
  if (!text) return null;
  return (
    <div>
      <Typography variant="body2" sx={FIELD_LABEL_SX}>
        {label}
      </Typography>
      <Typography sx={{ lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{text}</Typography>
    </div>
  );
}

TextField.propTypes = { label: PropTypes.string, text: PropTypes.string };
TextField.defaultProps = { label: "", text: "" };

/** 單一告警：預設只顯示一行摘要，點開才看說明、建議與受影響網址 */
function AlertItem({ alert: a, first }) {
  const [open, setOpen] = useState(false);
  const risk = RISK[Number(a.risk)] ? Number(a.risk) : 0;
  const refs = stripHtml(a.reference)
    .split(/\s*\n\s*/)
    .filter(Boolean);
  const urls = Array.isArray(a.urls) ? a.urls.slice(0, 5) : [];
  const cwe = Number(a.cwe_id);
  const wasc = Number(a.wasc_id);

  return (
    <Box sx={first ? {} : { borderTop: 1, borderColor: "divider" }}>
      <ButtonBase
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1.25,
          py: 1.25,
          minHeight: "var(--t-control-h)",
          color: "text.primary",
          textAlign: "left",
          "&:hover": { color: "primary.main" },
        }}
      >
        <MetaPill meta={RISK[risk]} />
        <Typography component="span" sx={{ flex: 1, minWidth: 0, fontWeight: 700, wordBreak: "break-word", color: "inherit" }}>
          {a.name}
        </Typography>
        <Typography
          component="span"
          variant="body2"
          color="text.secondary"
          sx={{ fontWeight: 700, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
        >
          {fmtCount(a.count)} 處
        </Typography>
        {a.confidence && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
            信心度 {CONFIDENCE[a.confidence] || a.confidence}
          </Typography>
        )}
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            display: "flex",
            flexShrink: 0,
            color: "text.secondary",
            transition: "transform .15s",
            transform: `rotate(${open ? 90 : 0}deg)`,
          }}
        >
          <MdChevronRight />
        </Box>
      </ButtonBase>
      {open && (
        <Stack spacing={1.25} sx={{ pt: 0.25, pb: 1.75, pl: 0.25 }}>
          <TextField label="說明" text={stripHtml(a.description)} />
          <TextField label="建議修正" text={stripHtml(a.solution)} />
          {refs.length > 0 && (
            <div>
              <Typography variant="body2" sx={FIELD_LABEL_SX}>
                參考資料
              </Typography>
              {refs.map((line, i) => {
                const href = safeHttpUrl(line);
                return (
                  <Typography key={`${i}-${line}`} variant="body2" sx={{ lineHeight: 1.6, wordBreak: "break-all" }}>
                    {href ? (
                      <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" sx={LINK_SX}>
                        {line}
                      </Link>
                    ) : (
                      line
                    )}
                  </Typography>
                );
              })}
            </div>
          )}
          {urls.length > 0 && (
            <div>
              {/* 受影響網址只列文字不做連結：裡面可能是會改資料的端點，誤點不該就送出請求 */}
              <Typography variant="body2" sx={FIELD_LABEL_SX}>
                受影響網址{Number(a.count) > urls.length ? `（列出 ${urls.length} 筆，共 ${a.count} 處）` : ""}
              </Typography>
              {urls.map((u, i) => (
                <Typography
                  key={`${i}-${u}`}
                  sx={{ ...MONO, fontSize: "var(--t-code)", lineHeight: 1.6, wordBreak: "break-all" }}
                >
                  {u}
                </Typography>
              ))}
            </div>
          )}
          {(cwe > 0 || wasc > 0 || a.plugin_id) && (
            <Typography
              component="div"
              variant="body2"
              color="text.secondary"
              sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", fontWeight: 600 }}
            >
              {cwe > 0 && (
                <Link
                  href={`https://cwe.mitre.org/data/definitions/${cwe}.html`}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={LINK_SX}
                >
                  CWE-{cwe}
                </Link>
              )}
              {wasc > 0 && <span>WASC-{wasc}</span>}
              {a.plugin_id && <span>ZAP 規則 {a.plugin_id}</span>}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}

AlertItem.propTypes = {
  alert: PropTypes.shape({
    plugin_id: PropTypes.string,
    name: PropTypes.string,
    risk: PropTypes.number,
    confidence: PropTypes.string,
    count: PropTypes.number,
    cwe_id: PropTypes.number,
    wasc_id: PropTypes.number,
    description: PropTypes.string,
    solution: PropTypes.string,
    reference: PropTypes.string,
    urls: PropTypes.arrayOf(PropTypes.string),
  }),
  first: PropTypes.bool,
};

AlertItem.defaultProps = { alert: {}, first: false };

/** 單一網址的結果卡 */
function TargetResultCard({ result: r, downloading, onDownload }) {
  const st = Number(r.status);
  // 依風險高 → 低，同風險再依件數多 → 少：最該先處理的永遠在最上面
  const alerts = useMemo(
    () =>
      (Array.isArray(r.alerts) ? [...r.alerts] : []).sort(
        (x, y) => Number(y.risk) - Number(x.risk) || Number(y.count) - Number(x.count)
      ),
    [r.alerts]
  );
  const statusLabel = st === 1 && r.stage ? `${ITEM_STATUS[1].label}・${r.stage}` : "";
  const hasCounts = RISK_FIELDS.some((f) => r[f.key] !== null && r[f.key] !== undefined);
  const href = safeHttpUrl(r.target_url);

  return (
    <Card sx={{ bgcolor: "background.default", overflow: "visible" }}>
      <Stack spacing={1.5} sx={{ px: 2, py: 1.75 }}>
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            {href ? (
              <Link
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ ...MONO, ...LINK_SX, fontSize: "var(--t-code)", fontWeight: 700 }}
              >
                {r.target_url}
              </Link>
            ) : (
              <Typography sx={{ ...MONO, fontSize: "var(--t-code)", fontWeight: 700, wordBreak: "break-all" }}>
                {r.target_url}
              </Typography>
            )}
            {(r.started_at || r.finished_at) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, fontVariantNumeric: "tabular-nums" }}>
                開始 {fmtDateTime(r.started_at)} · 結束 {fmtDateTime(r.finished_at)}
              </Typography>
            )}
          </Box>
          <MetaPill meta={ITEM_STATUS[st]} dot={st === 1} label={statusLabel} />
        </Box>

        {(hasCounts || r.has_report) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            {hasCounts && <RiskCounts row={r} />}
            <Box sx={{ flex: 1 }} />
            {r.has_report && (
              <Button variant="outlined" onClick={() => onDownload(r)}
                disabled={downloading}
                loading={downloading}
                loadingPosition="start"
                startIcon={<MdDownload />}
              >
                {downloading ? "下載中…" : "下載 HTML 報告"}
              </Button>
            )}
          </Box>
        )}

        <ErrorNote text={r.error_message || ""} />
      </Stack>

      <Box sx={{ px: 2, pb: 1 }}>
        <Box sx={{ borderTop: 1, borderColor: "divider", pt: 1.25, pb: 0.5 }}>
          <Typography sx={{ fontWeight: 700 }}>
            告警{" "}
            <Typography component="span" color="text.disabled" sx={{ fontVariantNumeric: "tabular-nums" }}>
              {alerts.length}
            </Typography>
          </Typography>
          {alerts.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1, fontWeight: 600 }}>
              {st === 2 ? "未發現告警" : "無告警資料"}
            </Typography>
          ) : (
            <Box sx={{ mt: 0.25 }}>
              {alerts.map((a, i) => (
                <AlertItem key={`${a.plugin_id || "alert"}-${i}`} alert={a} first={i === 0} />
              ))}
            </Box>
          )}
        </Box>
        <Disclosure label="執行紀錄">
          <LogTail text={r.log_tail || ""} />
        </Disclosure>
      </Box>
    </Card>
  );
}


TargetResultCard.propTypes = {
  result: PropTypes.shape({
    id: PropTypes.number,
    target_url: PropTypes.string,
    status: PropTypes.number,
    stage: PropTypes.string,
    has_report: PropTypes.bool,
    started_at: PropTypes.string,
    finished_at: PropTypes.string,
    error_message: PropTypes.string,
    log_tail: PropTypes.string,
    alerts: PropTypes.array,
  }),
  downloading: PropTypes.bool,
  onDownload: PropTypes.func,
};

TargetResultCard.defaultProps = { result: {}, downloading: false, onDownload: () => {} };

function VulnReportDrawer({ open, runId, onClose }) {
  const { toast } = useApp();
  const { report, loading, error } = useRunReport(BASE, open ? runId : null);
  const [downloadingId, setDownloadingId] = useState(null);
  const run = report && report.run ? report.run : null;
  const proj = report && report.project ? report.project : null;
  const results = report && Array.isArray(report.results) ? report.results : [];

  /**
   * 下載 ZAP HTML 報告 —— 同 Project/Detail.js 的 downloadDoc：
   * /VulnScan/ReportFile 要 Bearer token，直接開 <a href> 一定 401，所以取 blob 再存檔。
   */
  const downloadReport = async (result) => {
    if (downloadingId || !run) return;
    setDownloadingId(result.id);
    try {
      const blob = await request.get(`${BASE}/ReportFile/${result.id}`, null, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ZAP-${(proj && proj.code) || "project"}-run${run.id}-${result.id}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      /*
       * responseType 是 blob，錯誤回應（404「報告檔不存在」、400 下載失敗）也會變成 Blob，
       * apiErrorMessage 讀不到 res_msg，只會顯示 axios 的英文訊息。先讀回文字、解析 JSON 放回去
       */
      const data = err && err.response ? err.response.data : null;
      if (typeof Blob !== "undefined" && data instanceof Blob) {
        try {
          err.response.data = JSON.parse(await data.text());
        } catch (parseErr) {
          // 不是 JSON（例如代理伺服器的 HTML 錯誤頁）：拿掉 Blob，交給狀態碼判斷
          err.response.data = null;
        }
      }
      toast(apiErrorMessage(err, "下載報告失敗"), "error");
    } finally {
      setDownloadingId(null);
    }
  };

  let body;
  if (error) {
    body = <EmptyState title="報告載入失敗" text={error} />;
  } else if (!run) {
    body = loading ? <PageLoading dense /> : <EmptyState dense text="找不到此報告" />;
  } else {
    const mode = MODE[Number(run.scan_mode)] || MODE[0];
    body = (
      <Stack spacing={2}>
        <MetaGrid
          items={[
            { label: "模式", value: mode.label },
            { label: "排入", value: fmtDateTime(run.queued_at) },
            { label: "開始", value: fmtDateTime(run.started_at) },
            { label: "結束", value: fmtDateTime(run.finished_at) },
            { label: "耗時", value: fmtDuration(run.duration_seconds) },
            { label: "觸發者", value: run.create_name || run.create_id || "—" },
          ]}
        />
        <ErrorNote text={run.error_message || ""} />
        <KpiRow
          min={110}
          items={[
            {
              key: "targets",
              label: "網址成功",
              value: `${run.success_count}/${run.target_count}`,
              color: Number(run.failed_count) > 0 ? "warning.main" : undefined,
              hint: Number(run.failed_count) > 0 ? `${run.failed_count} 個網址掃描失敗` : "",
            },
            ...RISK_FIELDS.map((f) => ({
              key: f.key,
              label: f.label,
              value: fmtCount(run[f.key]),
              color: riskColor(run[f.key], f.risk),
            })),
          ]}
        />
        <Typography sx={{ fontWeight: 800 }}>
          各網址結果{" "}
          <Typography component="span" color="text.disabled">
            {results.length}
          </Typography>
        </Typography>
        {results.length === 0 ? (
          <EmptyState dense text="本次掃描無網址結果" />
        ) : (
          results.map((r) => (
            <TargetResultCard key={r.id} result={r} downloading={downloadingId === r.id} onDownload={downloadReport} />
          ))
        )}
      </Stack>
    );
  }

  return (
    <ScanDrawer
      open={open}
      onClose={onClose}
      title={`弱點掃描報告 #${run ? run.id : runId || ""}`}
      subtitle={proj ? [proj.code, proj.name].filter(Boolean).join(" · ") : ""}
      badges={
        run ? (
          <>
            <RunStatusPill
              status={Number(run.status)}
              durationSeconds={run.duration_seconds == null ? null : Number(run.duration_seconds)}
              cancelRequested={Boolean(run.cancel_requested)}
            />
            <MetaPill meta={(MODE[Number(run.scan_mode)] || MODE[0]).pill} />
          </>
        ) : null
      }
    >
      {body}
    </ScanDrawer>
  );
}

VulnReportDrawer.propTypes = {
  open: PropTypes.bool,
  runId: PropTypes.number,
  onClose: PropTypes.func,
};

VulnReportDrawer.defaultProps = { open: false, runId: null, onClose: () => {} };

/* ===================== 頁面 ===================== */

function VulnScanPage() {
  const { toast, confirmDelete } = useApp();
  const { projects, projectsLoaded, projId, project, setProjId, runId, openRun, closeRun } = useScanPage(BASE);
  const pid = project ? Number(project.id) : null;
  const runs = useRunHistory(BASE, pid);

  /* 沒權限就不放按鈕 —— 按了只會 403。權限碼與後端 VulnScanController 一字不差 */
  const canCreate = hasPermission("vulnscan.create");
  const canEdit = hasPermission("vulnscan.edit");
  const canDelete = hasPermission("vulnscan.delete");

  /* ---------- 掃描網址 ---------- */
  const [targetData, setTargetData] = useState(null); // { project, targets }；null = 還沒載入
  const [targetFailed, setTargetFailed] = useState(false);
  const [draft, setDraft] = useState("");
  const [mode, setMode] = useState(0);
  const [busy, setBusy] = useState(false);

  /** 用後端回傳的清單重填 textarea，看得到去掉結尾斜線、去重後實際會掃的網址 */
  const applyTargets = useCallback((d) => {
    const targets = Array.isArray(d && d.targets) ? d.targets : [];
    setTargetData({ project: (d && d.project) || null, targets });
    setDraft(targetsToText(targets));
  }, []);

  useEffect(() => {
    setTargetData(null);
    setTargetFailed(false);
    setDraft("");
    if (!pid) return undefined;
    let alive = true;
    apiGet(`${BASE}/Targets`, { projectId: pid })
      .then((d) => {
        if (alive) applyTargets(d);
      })
      .catch((e) => {
        if (!alive) return;
        setTargetFailed(true);
        toast(apiErrorMessage(e, "載入掃描網址失敗"), "error");
      });
    return () => {
      alive = false;
    };
  }, [pid, applyTargets, toast]);

  /* 掃描從進行中變成結束時，重抓清單讓每個網址的「最近一次結果」跟上；只換清單不動 textarea */
  const wasActiveRef = useRef({ pid: null, active: false });
  useEffect(() => {
    const prev = wasActiveRef.current;
    wasActiveRef.current = { pid, active: runs.hasActive };
    if (!pid || prev.pid !== pid || !prev.active || runs.hasActive) return;
    apiGet(`${BASE}/Targets`, { projectId: pid })
      .then((d) => {
        if (!d || !Array.isArray(d.targets)) return;
        setTargetData((cur) => (cur ? { ...cur, targets: d.targets } : cur));
      })
      .catch(() => {});
  }, [pid, runs.hasActive]);

  const savedTargets = targetData ? targetData.targets : [];
  const dirty = targetData != null && normalizedDraft(draft) !== targetsToText(savedTargets);

  const saveTargets = () => {
    if (!pid || busy || !targetData) return;
    const lines = parseListLines(draft);
    // 後端也會擋，但前端先講出「第幾行」，使用者不必自己一行一行找
    const bad = lines.find((x) => !/^https?:\/\//i.test(x.url));
    if (bad) {
      toast(`第 ${bad.line} 行的網址須以 http:// 或 https:// 開頭`, "error");
      return;
    }
    const targets = lines.map((x) => ({ url: x.url, name: x.rest }));

    const doSave = async () => {
      setBusy(true);
      try {
        const d = await apiPutJson(`${BASE}/Targets`, { projectId: pid, targets });
        applyTargets(d);
        const n = Array.isArray(d && d.targets) ? d.targets.length : targets.length;
        toast(n ? `已儲存 ${n} 個掃描網址` : "已清空掃描網址");
      } catch (e) {
        toast(apiErrorMessage(e, "儲存掃描網址失敗"), "error");
      } finally {
        setBusy(false);
      }
    };

    if (targets.length < savedTargets.length) {
      confirmDelete({
        title: "移除部分掃描網址？",
        subtitle: `清單由 ${savedTargets.length} 個變更為 ${targets.length} 個`,
        body: "不在清單上的網址之後將不再掃描；過去的掃描紀錄與報告仍會保留。",
        confirmText: "儲存",
        onConfirm: doSave,
      });
      return;
    }
    doSave();
  };

  /* ---------- 掃描動作 ---------- */

  const startScan = () => {
    if (!pid || busy) return;
    const scanMode = mode;
    const doStart = async () => {
      setBusy(true);
      try {
        await apiPostJson(`${BASE}/Run`, { projectId: pid, mode: scanMode });
        toast(`已排入${MODE[scanMode].label}，完成後將通知您`);
        runs.showFirstPage();
      } catch (e) {
        toast(apiErrorMessage(e, "排入掃描失敗"), "error");
      } finally {
        setBusy(false);
      }
    };
    // 完整掃描會真的對目標發動攻擊測試：誤掃正式環境或第三方系統的後果很嚴重，一定再問一次
    if (scanMode === 1) {
      confirmDelete({
        title: "開始完整掃描？",
        subtitle: "將對網站送出攻擊測試請求",
        body: "完整掃描將對網站送出大量攻擊測試請求，可能產生測試資料或影響服務效能。僅限掃描公司自有且已取得授權的系統，建議於測試環境執行。",
        confirmText: "開始完整掃描",
        onConfirm: doStart,
      });
      return;
    }
    doStart();
  };

  const cancelRun = (r) =>
    confirmDelete({
      title: "停止本次掃描？",
      subtitle: r.status === 0 ? "排隊中，將直接取消" : "執行中，將於目前網址的步驟結束後停止",
      body: "已完成的網址結果將保留，尚未執行的網址將標示為已略過。",
      confirmText: "停止掃描",
      onConfirm: async () => {
        try {
          const d = await apiPostJson(`${BASE}/Run/${r.id}/Cancel`, {});
          // 以後端實際做了什麼為準：畫面上的狀態可能已經過時（例如掃描主機早就斷線，後端會直接標為失敗）
          let result = d && d.result;
          if (!result) result = r.status === 0 ? "cancelled" : "requested";
          if (result === "failed") toast("掃描主機逾時未回報，本次掃描已標示為失敗");
          else if (result === "cancelled") toast("已取消掃描");
          else toast("已要求停止，掃描主機將於目前步驟結束後停止");
          runs.refresh();
        } catch (e) {
          toast(apiErrorMessage(e, "取消掃描失敗"), "error");
        }
      },
    });

  const deleteRun = (r) =>
    confirmDelete({
      title: "刪除掃描紀錄？",
      subtitle: "此操作無法復原",
      body: `確定要刪除 ${fmtDateTime(r.queued_at)} 的掃描紀錄嗎？HTML 報告檔將一併刪除。`,
      onConfirm: async () => {
        try {
          await apiDelete(`${BASE}/Run/${r.id}`);
          toast("已刪除掃描紀錄");
          if (runId === r.id) closeRun();
          runs.refresh();
        } catch (e) {
          toast(apiErrorMessage(e, "刪除掃描紀錄失敗"), "error");
        }
      },
    });

  /* 「開始掃描」停用原因：直接放在按鈕 title */
  let startBlock = "";
  if (!targetData) startBlock = "掃描網址載入中…";
  else if (savedTargets.length === 0) startBlock = "請先設定掃描網址並儲存";
  else if (runs.hasActive) startBlock = "此專案已有掃描正在排隊或執行中";
  else if (dirty) startBlock = "掃描網址有未儲存的變更，請先儲存";
  else if (busy) startBlock = "處理中…";

  const latest = runs.latest;

  /* ---------- 版面 ---------- */

  const historyBody = () => {
    const stateRow = (title) => (
      <TableRow>
        <TableCell colSpan={11}>
          <EmptyState dense text={title} />
        </TableCell>
      </TableRow>
    );
    if (runs.loading && runs.items.length === 0)
      return (
        <TableRow>
          <TableCell colSpan={11}>
            <SkeletonRows rows={5} />
          </TableCell>
        </TableRow>
      );
    if (runs.failed) return stateRow("掃描歷史載入失敗，請稍後再試");
    if (runs.items.length === 0) return stateRow("尚無掃描紀錄，請設定網址後點選右上方「開始掃描」");
    return runs.items.map((r) => {
      const active = isActiveStatus(r.status);
      const m = MODE[Number(r.scan_mode)] || MODE[0];
      return (
        <TableRow key={r.id}>
          <TableCell>{fmtDateTime(r.queued_at)}</TableCell>
          <TableCell>{r.create_name || r.create_id || "—"}</TableCell>
          <TableCell>
            <MetaPill meta={m.pill} title={m.desc} />
          </TableCell>
          <TableCell>
            <RunStatusPill status={r.status} durationSeconds={r.duration_seconds} cancelRequested={r.cancel_requested} />
          </TableCell>
          <TableCell align="right">{fmtDuration(r.duration_seconds)}</TableCell>
          <TableCell align="right">
            {r.success_count}/{r.target_count}
            {Number(r.failed_count) > 0 && (
              <Box component="span" sx={{ color: "error.main", fontWeight: 700, ml: 0.75 }}>
                失敗 {r.failed_count}
              </Box>
            )}
          </TableCell>
          {/* 只有高風險標紅：中低風險幾乎每個網站都有，全部上色就等於沒有重點 */}
          <TableCell align="right" sx={Number(r.high_count) > 0 ? { color: "error.main !important", fontWeight: 800 } : undefined}>
            {fmtCount(r.high_count)}
          </TableCell>
          <TableCell align="right">{fmtCount(r.medium_count)}</TableCell>
          <TableCell align="right">{fmtCount(r.low_count)}</TableCell>
          <TableCell align="right">{fmtCount(r.info_count)}</TableCell>
          <TableCell sx={{ py: 0.5 }}>
            <Box sx={{ display: "flex", gap: 0.25, justifyContent: "flex-end" }}>
              <IconButton icon="view" title="檢視報告" onClick={() => openRun(r.id)} />
              {canCreate && active && (
                <IconButton
                  icon="cancel"
                  title={r.cancel_requested ? "已要求停止，等待掃描主機停止" : "停止掃描"}
                  disabled={r.cancel_requested}
                  onClick={() => cancelRun(r)}
                />
              )}
              {canDelete && !active && <IconButton icon="delete" title="刪除紀錄" onClick={() => deleteRun(r)} />}
            </Box>
          </TableCell>
        </TableRow>
      );
    });
  };

  const renderMain = () => {
    if (!projId) {
      return (
        <Card>
          {projectsLoaded && projects.length === 0 ? (
            <EmptyState title="沒有可掃描的專案" text="目前沒有可檢視的專案；請專案經理將您加入專案成員。" />
          ) : (
            <EmptyState title="請先選擇專案" text="選擇專案後，即可設定掃描網址並開始掃描。" />
          )}
        </Card>
      );
    }
    if (!project) {
      return (
        <Card>
          {projectsLoaded ? (
            <EmptyState title="找不到此專案" text="專案可能已刪除，或不在您的檢視範圍內。" />
          ) : (
            <PageLoading dense />
          )}
        </Card>
      );
    }

    return (
      <>
        {/* 掃描網址 */}
        <Card>
          <SectionHead>
            <span>掃描網址</span>
            <CountChip>已儲存 {savedTargets.length} 個網址</CountChip>
            {dirty && <HeadNote color="warning.main">有未儲存的變更</HeadNote>}
            <Box sx={{ flex: 1 }} />
            {canEdit && (
              <Button variant="outlined" onClick={saveTargets} disabled={busy || !dirty}>
                儲存
              </Button>
            )}
          </SectionHead>
          <Stack spacing={1.25} sx={{ px: 3, py: 2 }}>
            {targetFailed ? (
              <EmptyState dense text="掃描網址載入失敗，請重新整理後再試" />
            ) : (
              <MuiTextField
                multiline
                minRows={5}
                fullWidth
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={targetData ? "https://test.example.com 測試站\nhttps://api-test.example.com" : "載入中…"}
                title={canEdit ? "" : "您沒有維護掃描網址的權限"}
                slotProps={{
                  htmlInput: { readOnly: !canEdit || !targetData, spellCheck: false, "aria-label": "掃描網址", "aria-describedby": "vulnscan-target-help" },
                }}
                sx={{ "& textarea": { ...MONO, fontSize: "var(--t-code)" } }}
              />
            )}
            <Typography id="vulnscan-target-help" variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              每行一筆，格式為「網址 名稱」（名稱可省略）；# 開頭為註解。僅接受 http:// 或 https://，不可包含帳號密碼。
            </Typography>
          </Stack>
          {savedTargets.length > 0 && (
            <Box sx={{ borderTop: 1, borderColor: "divider" }}>
              {savedTargets.map((t) => {
                const last = t.last;
                const lastSt = last ? Number(last.status) : null;
                return (
                  <Typography
                    component="div"
                    variant="body2"
                    key={t.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      flexWrap: "wrap",
                      px: 3,
                      py: 1.25,
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Box
                      component="span"
                      title={t.url}
                      sx={{ ...MONO, flex: 1, minWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {t.url}
                    </Box>
                    {t.name && (
                      <Box component="span" sx={{ color: "text.secondary", fontWeight: 600 }}>
                        {t.name}
                      </Box>
                    )}
                    {!last && (
                      <Box component="span" sx={{ color: "text.disabled", fontWeight: 600 }}>
                        尚未掃描
                      </Box>
                    )}
                    {last && lastSt === 2 && <RiskCounts row={last} />}
                    {last && lastSt !== 2 && <MetaPill meta={ITEM_STATUS[lastSt]} dot={lastSt === 1} />}
                    {last && (
                      <Box component="span" sx={{ color: "text.disabled", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                        {fmtDateTime(last.finished_at)}
                      </Box>
                    )}
                  </Typography>
                );
              })}
            </Box>
          )}
        </Card>

        {/* 最新結果 */}
        <Card>
          <SectionHead>
            <span>最新結果</span>
            {latest && (
              <HeadNote>
                {fmtDateTime(latest.finished_at || latest.queued_at)} · {(MODE[Number(latest.scan_mode)] || MODE[0]).label} ·{" "}
                {latest.success_count}/{latest.target_count} 個網址成功
              </HeadNote>
            )}
            <Box sx={{ flex: 1 }} />
            {latest && (
              <Button variant="outlined" onClick={() => openRun(latest.id)}>
                檢視報告
              </Button>
            )}
          </SectionHead>
          <Box sx={{ px: 3, py: 2 }}>
            {latest ? (
              <KpiRow
                items={RISK_FIELDS.map((f) => ({
                  key: f.key,
                  label: f.label,
                  value: fmtCount(latest[f.key]),
                  color: riskColor(latest[f.key], f.risk),
                }))}
              />
            ) : (
              <EmptyState dense text="尚無已完成的掃描" />
            )}
          </Box>
        </Card>

        {/* 掃描歷史 */}
        <Card>
          <SectionHead>
            <span>掃描歷史</span>
            <CountChip>共 {runs.total} 次</CountChip>
            {runs.hasActive && <HeadNote>有掃描進行中，每 5 秒自動更新</HeadNote>}
          </SectionHead>
          <TableContainer>
            <Table sx={{ minWidth: 1000 }}>
              <TableHead sx={{ bgcolor: "background.default" }}>
                <TableRow>
                  <TableCell>開始時間</TableCell>
                  <TableCell>觸發者</TableCell>
                  <TableCell>模式</TableCell>
                  <TableCell>狀態</TableCell>
                  <TableCell align="right">耗時</TableCell>
                  <TableCell align="right">網址（成功/總數）</TableCell>
                  <TableCell align="right">高</TableCell>
                  <TableCell align="right">中</TableCell>
                  <TableCell align="right">低</TableCell>
                  <TableCell align="right">資訊</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={tableBodySx}>{historyBody()}</TableBody>
            </Table>
          </TableContainer>
          <ListPager
            page={runs.page}
            totalPages={runs.totalPages}
            total={runs.total}
            onPrev={() => runs.setPage(runs.page - 1)}
            onNext={() => runs.setPage(runs.page + 1)}
          />
        </Card>
      </>
    );
  };

  return (
    <>
      <Stack spacing={2} className="pg">
        <Card sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", px: 2, py: 1.75 }}>
          <MuiTextField
            select
            label="專案"
            value={projId}
            onChange={(e) => setProjId(e.target.value)}
            slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
            sx={{ minWidth: 220, maxWidth: "100%" }}
          >
            <option value="">請選擇專案</option>
            {projects.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.code ? `${p.code} ${p.name}` : p.name}
              </option>
            ))}
          </MuiTextField>
          <Box sx={{ flex: 1 }} />
          {canCreate && project && (
            <>
              <MuiTextField
                select
                value={mode}
                onChange={(e) => setMode(Number(e.target.value))}
                title={`${MODE[mode].label}：${MODE[mode].desc}`}
                slotProps={{ select: { native: true }, htmlInput: { "aria-label": "掃描模式" } }}
                sx={{ maxWidth: "100%" }}
              >
                <option value={0}>基本掃描（不送出攻擊請求）</option>
                <option value={1}>完整掃描（含主動攻擊測試）</option>
              </MuiTextField>
              {/* 停用的按鈕收不到滑鼠事件，停用原因改由外層 span 的 Tooltip 顯示 */}
              <Tooltip title={startBlock || `${MODE[mode].label}已儲存的 ${savedTargets.length} 個網址`}>
                <span>
                  <Button variant="contained" onClick={startScan}
                    disabled={Boolean(startBlock)}
                    loading={busy}
                    loadingPosition="start"
                    startIcon={<MdPlayArrow />}
                  >
                    開始掃描
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
        </Card>

        {renderMain()}
      </Stack>

      {/* 深連結 ?run= 等專案清單載入後才開，底下的頁面先就位 */}
      <VulnReportDrawer open={Boolean(runId) && projectsLoaded} runId={runId} onClose={closeRun} />
    </>
  );
}

/* 狀態全在頁內與網址列，不接 redux，沒有外部 props（同 Project/Add.js 的寫法） */
VulnScanPage.propTypes = {};
VulnScanPage.defaultProps = {};

export default VulnScanPage;
